import * as React from "react";
import { ArrowLeft, Star, Plus } from "lucide-react";

import { Dialog } from "@/components/ui";
import { FanCreationForm } from "@/components/Forms/FanCreationForm";
import { FormPersonalizationHelpDialog } from "@/containers/Coach/Reputation/Forms/components/FormPersonalizationHelpDialog";

import { SimpleShareView } from "./components/SimpleShareView";
import { CustomizerWorkflowWizard } from "./components/CustomizerWorkflowWizard";
import { ReviewOutreachScriptDialog } from "./components/ReviewOutreachScriptDialog";
import RecordVideoDialog from "@/containers/RecordVideo/RecordVideoDialog";
import { VideoPageModal } from "@/containers/VideoPages";

import { ShareFormLinkProvider, useShareFormLink } from "./context/ShareFormLinkContext";
import { getFanFormDefaultValues } from "@/utils/fanHelpers";

interface ShareFormLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  playerId: number;
  formRequestId?: number;
  initialShowCustom?: boolean;
  initialFormId?: string | number;
  initialFan?: Fan;
  initialWizardStep?: number;
}

const ShareFormLinkDialogInner: React.FC = () => {
  const {
    open,
    onOpenChange,
    playerId,
    selectedRequest,
    showCustomWorkflow,
    isEditingRequest,
    isCreateFanOpen,
    setIsCreateFanOpen,
    isCreatingFan,
    isHelpOpen,
    setIsHelpOpen,
    activeFanSelected,
    showOutreachScriptDialog,
    setShowOutreachScriptDialog,
    isGeneratingOutreachScript,
    outreachScriptText,
    showRecordDialog,
    setShowRecordDialog,
    recordedMedia,
    setRecordedMedia,
    showVideoPageModal,
    setShowVideoPageModal,
    handleContinueToRecording,
    handleVideoRecordingDone,
    handleCancelCustomWorkflow,
    handleFanCreated,
    initialShowCustom,
  } = useShareFormLink();

  const fanDetails = activeFanSelected?.original || {};
  const isExistingFan = activeFanSelected?.id && !String(activeFanSelected.id).startsWith("temp-");

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange} size={showCustomWorkflow ? "md" : "xl"} showCloseButton>
        <Dialog.Header>
          <Dialog.Title className="flex items-center gap-2 text-slate-800 font-bold select-none">
            {showCustomWorkflow && !initialShowCustom ? (
              <button
                onClick={handleCancelCustomWorkflow}
                className="p-1 hover:bg-slate-100 rounded-md mr-1 transition-colors border-none bg-transparent cursor-pointer"
              >
                <ArrowLeft className="size-4 text-slate-500 shrink-0" />
              </button>
            ) : (
              <Star className="size-5 text-indigo-600 shrink-0 fill-indigo-600/10" />
            )}
            {showCustomWorkflow ? (isEditingRequest ? "Edit Personalization" : "Customize Link") : "Share Review Link"}
          </Dialog.Title>
        </Dialog.Header>

        <Dialog.Content className="flex flex-col items-center gap-5">
          {showCustomWorkflow ? <CustomizerWorkflowWizard /> : <SimpleShareView />}
        </Dialog.Content>
      </Dialog>

      {/* Nested Create New Fan Dialog */}
      <Dialog open={isCreateFanOpen} onOpenChange={setIsCreateFanOpen} size="lg" showCloseButton>
        <Dialog.Header>
          <Dialog.Title className="flex items-center gap-2 text-base font-bold font-sans select-none">
            <Plus className="size-5 text-primary" />
            <span>{isExistingFan ? "Edit Fan Details" : "Create New Fan"}</span>
          </Dialog.Title>
          <Dialog.Description className="select-none text-xs">
            {isExistingFan
              ? "Update the information for this fan contact."
              : "Add a new fan to your dashboard. This contact will be automatically assigned to you."}
          </Dialog.Description>
        </Dialog.Header>

        <Dialog.Content className="max-h-[60vh] overflow-y-auto py-4 px-6">
          <FanCreationForm
            onSubmit={handleFanCreated}
            isSubmitting={isCreatingFan}
            hidePlayerSelect={true}
            submitLabel={isExistingFan ? "Save Changes" : "Create Fan"}
            defaultValues={getFanFormDefaultValues(fanDetails, playerId, activeFanSelected?.label)}
          />
        </Dialog.Content>
      </Dialog>

      {/* Form Personalization Help Dialog */}
      <FormPersonalizationHelpDialog open={isHelpOpen} onOpenChange={setIsHelpOpen} />

      {/* Review Outreach Video Script Dialog */}
      {showOutreachScriptDialog && (
        <ReviewOutreachScriptDialog
          open={showOutreachScriptDialog}
          onOpenChange={setShowOutreachScriptDialog}
          generatedScript={outreachScriptText}
          isGenerating={isGeneratingOutreachScript}
          onContinueToRecording={handleContinueToRecording}
          fanId={activeFanSelected?.id || selectedRequest?.fan?.id}
        />
      )}

      {/* Inline Recording Dialog */}
      {showRecordDialog && (
        <RecordVideoDialog
          open={showRecordDialog}
          initialScript={outreachScriptText}
          initialRecordVideoType="teleprompter"
          onOpenChange={setShowRecordDialog}
          onDone={handleVideoRecordingDone}
        />
      )}

      {/* Inline Video Page Creation Modal */}
      {showVideoPageModal && recordedMedia && (
        <VideoPageModal
          open={showVideoPageModal}
          initialObject={{ media: recordedMedia }}
          formRequestId={selectedRequest?.id}
          autoOpenShare={true}
          onClose={() => {
            setShowVideoPageModal(false);
            setRecordedMedia(null);
            onOpenChange(false); // close the master dialog as well since outreach page is created!
          }}
        />
      )}
    </>
  );
};

export const ShareFormLinkDialog: React.FC<ShareFormLinkDialogProps> = ({
  open,
  onOpenChange,
  playerId,
  formRequestId,
  initialShowCustom = false,
  initialFormId,
  initialFan,
  initialWizardStep,
}) => {
  return (
    <ShareFormLinkProvider
      open={open}
      onOpenChange={onOpenChange}
      playerId={playerId}
      formRequestId={formRequestId}
      initialShowCustom={initialShowCustom}
      initialFormId={initialFormId}
      initialFan={initialFan}
      initialWizardStep={initialWizardStep}
    >
      <ShareFormLinkDialogInner />
    </ShareFormLinkProvider>
  );
};
